/* eslint-disable react/style-prop-object */
import React, { useEffect, useState, useCallback, useRef } from 'react';

// ── Scale helper ──────────────────────────────────────────────────────────────
// All pixel values are authored for 1920×1080.
// Silk on FireStick HD reports screen.width=960, so _scale=0.5 and everything
// halves automatically. On LG (screen.width=1920) _scale=1.0 — no change.
const DESIGN_WIDTH = 1920;
const _screenW = typeof window !== 'undefined' ? (window.screen?.width ?? DESIGN_WIDTH) : DESIGN_WIDTH;
const _scale = Math.min(1, _screenW / DESIGN_WIDTH); // never scale UP
function s(px: number): number { return Math.round(px * _scale); }

// ── Types ────────────────────────────────────────────────────────────────────

interface CameraSponsorView {
    cameraId: string;
    cameraLabel: string;
    sponsorName: string;
    logoUrl: string;
}

interface CameraView {
    id: string;
    label: string;
    hlsUrl: string;
    iframeUrl: string;
    isLive: boolean;
    sortOrder: number;
    isFeatured: boolean;
}

interface SponsorView {
    id: string;
    name: string;
    logoUrl: string;
    clickUrl?: string;
    durationSeconds: number;
}

interface LeaderboardEntryDto {
    position: number;
    playerName: string;
    scoreToPar: number;
    stablefordPoints?: number;
    holesPlayed: number;
    currentHole?: string;
}

interface ScoringEventDto {
    id: string;
    playerName: string;
    holeName: string;
    scoreType: 'Par' | 'Birdie' | 'Eagle' | 'Albatross' | 'HoleInOne';
    recordedAt: string;
}

interface DisplaySettingsDto {
    leaderboardRotationMinutes: number;
    leaderboardDurationSeconds: number;
    leaderboardStyle: 'FullScreen' | 'Overlay';
    cameraRotationSeconds: number;
    inactivityThresholdSeconds: number;
    tickerMode: 'AlwaysOn' | 'SlideOnEvent';
    tickerSlideFrequencySeconds: number;
    showPars: boolean;
    showBirdies: boolean;
    showEagles: boolean;
}

interface ActiveCompetitionDto {
    id: string;
    name: string;
    format: string;
    type: string;
    isAdhoc: boolean;
    isStreamingWindow: boolean;
    sponsors: SponsorView[];
    recentScoringEvents: ScoringEventDto[];
    leaderboard: LeaderboardEntryDto[];
    displaySettings: DisplaySettingsDto;
    cameraSponsors: CameraSponsorView[];
}

interface NowPlayingData {
    type: string;
    label: string;
    holeNumber: number | null;
    par: number | null;
    distanceMetres: number | null;
    playUrl: string | null;
    updatedAt: string;
}

interface DisplayConfig {
    clubName: string;
    clubSlug: string;
    primaryColour: string;
    logoUrl: string;
    heroImageUrl: string;
    cameras: CameraView[];
    sponsors: SponsorView[];
    leaderboard: any;
    scoreRefreshIntervalSeconds: number;
    activeCompetition?: ActiveCompetitionDto;
    streamingActive?: boolean;
}

// ── Config ────────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE || 'https://localhost:7220';
const CLUB_CODE = window.location.pathname.replace('/', '').split('/')[0] || 'joondalup-resort';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatScore(score: number, points?: number): string {
    if (points != null) return `${points}pts`;
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : `${score}`;
}

function scoreColour(score: number): string {
    if (score <= -2) return '#ffd700';
    if (score === -1) return '#4ade80';
    if (score === 0) return '#ffffff';
    return '#f97316';
}


// ── Birdie Animation ──────────────────────────────────────────────────────────

function BirdieAnimation({ event }: { event: ScoringEventDto }) {
    return (
        <div style={birdieStyles.scene}>
            {/* Notification card — positioned high so it doesn't block the action */}
            <div style={birdieStyles.card}>
                <div style={birdieStyles.label}>BIRDIE</div>
                <div style={birdieStyles.player}>{event.playerName}</div>
                <div style={birdieStyles.hole}>{event.holeName}</div>
            </div>

            {/* Flying bird — follows CSS keyframe path */}
            <div style={birdieStyles.birdFlight}>
                <div style={birdieStyles.birdWrap}>
                    <BirdieSvg />
                </div>
            </div>

            {/* Sparkle particles that puff at landing/takeoff */}
            <div style={birdieStyles.sparkleField}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} style={{
                        ...birdieStyles.sparkle,
                        left: `${48 + (i - 2.5) * 4}%`,
                        animationDelay: `${2.3 + i * 0.05}s`,
                    }} />
                ))}
            </div>
        </div>
    );
}

function BirdieSvg() {
    return (
        <svg width={s(80)} height={s(80)} viewBox="0 0 80 80" style={{ display: 'block' }}>
            {/* Body */}
            <ellipse cx="38" cy="44" rx="22" ry="18" fill="#1a6b3c" />
            {/* Belly */}
            <ellipse cx="38" cy="48" rx="14" ry="10" fill="#4ade80" opacity="0.85" />
            {/* Head */}
            <circle cx="56" cy="34" r="13" fill="#1a6b3c" />
            {/* Eye white */}
            <circle cx="59" cy="32" r="4" fill="#fff" />
            {/* Eye pupil */}
            <circle cx="60" cy="32" r="2" fill="#0a1a10" />
            {/* Beak */}
            <polygon points="67,34 76,33 67,38" fill="#c9a84c" />
            {/* Tail */}
            <polygon points="18,40 6,34 8,48" fill="#1a6b3c" />
            {/* Wing — animates via parent transform */}
            <g style={{ transformOrigin: '38px 38px', animation: 'birdieWing 0.35s ease-in-out infinite alternate' }}>
                <ellipse cx="34" cy="38" rx="14" ry="8" fill="#4ade80" transform="rotate(-15 34 38)" />
                <ellipse cx="34" cy="38" rx="10" ry="5" fill="#22c55e" transform="rotate(-15 34 38)" />
            </g>
        </svg>
    );
}

const birdieStyles: Record<string, React.CSSProperties> = {
    scene: {
        position: 'absolute', inset: 0, zIndex: 150,
        pointerEvents: 'none',
    },
    card: {
        position: 'absolute',
        top: '12%', left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, rgba(26,107,60,0.95), rgba(20,80,45,0.95))',
        border: `${s(3)}px solid #4ade80`,
        borderRadius: s(14),
        padding: `${s(20)}px ${s(40)}px`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 0 40px rgba(74,222,128,0.35)',
        minWidth: s(400),
        textAlign: 'center' as const,
        animation: 'birdieCardIn 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    label: {
        fontSize: s(22), fontWeight: 900, color: '#4ade80', letterSpacing: 6,
        marginBottom: s(10), textTransform: 'uppercase' as const,
        textShadow: '0 2px 8px rgba(0,0,0,0.4)',
    },
    player: {
        fontSize: s(36), fontWeight: 800, color: '#fff', marginBottom: s(6),
        textShadow: '0 2px 8px rgba(0,0,0,0.4)',
    },
    hole: {
        fontSize: s(18), color: 'rgba(255,255,255,0.9)', fontWeight: 600,
    },
    // The bird flies a curved path: bottom-left → onto card → idle → off bottom-right.
    // We use two nested layers: outer translates, inner does the curved bob.
    birdFlight: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(80), height: s(80),
        animation: 'birdiePath 10s linear forwards',
    },
    birdWrap: {
        width: '100%', height: '100%',
        animation: 'birdieBob 10s ease-in-out forwards',
    },
    sparkleField: {
        position: 'absolute',
        top: '15%', left: 0, right: 0,
        height: s(40),
    },
    sparkle: {
        position: 'absolute',
        top: 0,
        width: s(8), height: s(8),
        borderRadius: '50%',
        background: 'radial-gradient(circle, #fde047 0%, #4ade80 60%, transparent 100%)',
        opacity: 0,
        animation: 'birdieSparkle 1.2s ease-out forwards',
    },
};


// ── Eagle Animation ───────────────────────────────────────────────────────────

function EagleAnimation({ event }: { event: ScoringEventDto }) {
    return (
        <div style={eagleStyles.scene}>
            {/* Background gold glow pulse */}
            <div style={eagleStyles.bgGlow} />

            {/* Notification card — gold themed, with halo */}
            <div style={eagleStyles.card}>
                <div style={eagleStyles.halo} />
                <div style={eagleStyles.cardInner}>
                    <div style={eagleStyles.label}>EAGLE</div>
                    <div style={eagleStyles.player}>{event.playerName}</div>
                    <div style={eagleStyles.hole}>{event.holeName}</div>
                </div>
            </div>

            {/* Soaring eagle */}
            <div style={eagleStyles.eagleFlight}>
                <div style={eagleStyles.eagleTilt}>
                    <EagleSvg />
                </div>
            </div>

            {/* Gold particle trail in the eagle's wake */}
            <div style={eagleStyles.trailField}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
                    <div key={i} style={{
                        ...eagleStyles.trailParticle,
                        animationDelay: `${2.2 + i * 0.15}s`,
                    }} />
                ))}
            </div>

            {/* Burst sparkles when eagle passes the card */}
            <div style={eagleStyles.burstField}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => {
                    const angle = (i / 10) * Math.PI * 2;
                    const dx = Math.round(Math.cos(angle) * 80);
                    const dy = Math.round(Math.sin(angle) * 80);
                    return (
                        <div key={i} style={{
                            ...eagleStyles.burst,
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${5.5 + i * 0.04}s`,
                        } as React.CSSProperties} />
                    );
                })}
            </div>
        </div>
    );
}

function EagleSvg() {
    return (
        <svg width={s(160)} height={s(120)} viewBox="0 0 160 120" style={{ display: 'block' }}>
            {/* Tail feathers */}
            <polygon points="20,62 4,56 8,72 14,68" fill="#5a3a1f" />
            <polygon points="20,62 8,72 18,76" fill="#3d2811" />

            {/* Body */}
            <ellipse cx="80" cy="62" rx="28" ry="14" fill="#6b3f1d" />
            <ellipse cx="80" cy="66" rx="22" ry="9" fill="#8a5028" opacity="0.85" />

            {/* Left wing — outstretched gliding */}
            <path d="M 70 58 Q 40 32 8 42 Q 30 50 50 58 Q 60 62 70 62 Z"
                fill="#5a3a1f" />
            <path d="M 70 58 Q 45 40 18 46 Q 36 52 52 58 Q 60 60 70 60 Z"
                fill="#7a4d28" opacity="0.9" />
            {/* Wing tip feathers (left) */}
            <path d="M 18 44 L 10 38 L 14 46 Z" fill="#3d2811" />
            <path d="M 24 42 L 16 34 L 20 44 Z" fill="#3d2811" />
            <path d="M 30 42 L 24 32 L 28 42 Z" fill="#3d2811" />

            {/* Right wing — outstretched gliding */}
            <path d="M 90 58 Q 120 32 152 42 Q 130 50 110 58 Q 100 62 90 62 Z"
                fill="#5a3a1f" />
            <path d="M 90 58 Q 115 40 142 46 Q 124 52 108 58 Q 100 60 90 60 Z"
                fill="#7a4d28" opacity="0.9" />
            {/* Wing tip feathers (right) */}
            <path d="M 142 44 L 150 38 L 146 46 Z" fill="#3d2811" />
            <path d="M 136 42 L 144 34 L 140 44 Z" fill="#3d2811" />
            <path d="M 130 42 L 136 32 L 132 42 Z" fill="#3d2811" />

            {/* Head — white head, eagle-style */}
            <circle cx="106" cy="56" r="11" fill="#f5f0e8" />
            <circle cx="106" cy="56" r="9" fill="#fff" />

            {/* Eye */}
            <circle cx="111" cy="54" r="3" fill="#fde047" />
            <circle cx="112" cy="54" r="1.5" fill="#0a0a0a" />

            {/* Beak */}
            <path d="M 116 56 Q 124 56 122 60 Q 120 58 116 58 Z" fill="#fde047" />
            <path d="M 116 56 Q 124 56 122 60 Q 120 58 116 58 Z" fill="none" stroke="#c9a84c" strokeWidth="0.5" />
        </svg>
    );
}

const eagleStyles: Record<string, React.CSSProperties> = {
    scene: {
        position: 'absolute', inset: 0, zIndex: 160,
        pointerEvents: 'none',
    },
    bgGlow: {
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 25%, rgba(253,224,71,0.18) 0%, rgba(201,168,76,0.08) 30%, transparent 65%)',
        animation: 'eagleBgGlow 15s ease-in-out forwards',
    },
    card: {
        position: 'absolute',
        top: '10%', left: '50%',
        transform: 'translateX(-50%)',
        animation: 'eagleCardIn 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    halo: {
        position: 'absolute', inset: `-${s(20)}px`,
        borderRadius: s(28),
        background: 'radial-gradient(ellipse at center, rgba(253,224,71,0.4) 0%, transparent 70%)',
        animation: 'eagleHalo 15s ease-in-out infinite',
        pointerEvents: 'none',
    },
    cardInner: {
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(201,168,76,0.95), rgba(155,127,55,0.95))',
        border: `${s(4)}px solid #fde047`,
        borderRadius: s(16),
        padding: `${s(28)}px ${s(56)}px`,
        boxShadow: '0 16px 40px rgba(0,0,0,0.6), 0 0 60px rgba(253,224,71,0.4)',
        minWidth: s(520),
        textAlign: 'center' as const,
    },
    label: {
        fontSize: s(32), fontWeight: 900, color: '#fef9c3', letterSpacing: 8,
        marginBottom: s(14), textTransform: 'uppercase' as const,
        textShadow: '0 3px 10px rgba(0,0,0,0.5)',
    },
    player: {
        fontSize: s(48), fontWeight: 800, color: '#fff', marginBottom: s(8),
        textShadow: '0 3px 10px rgba(0,0,0,0.5)',
    },
    hole: {
        fontSize: s(22), color: 'rgba(255,255,255,0.95)', fontWeight: 600,
    },
    // Outer layer translates across the screen on a wide arc
    eagleFlight: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(160), height: s(120),
        animation: 'eaglePath 15s cubic-bezier(0.5, 0, 0.5, 1) forwards',
    },
    // Inner layer applies subtle banking (rotation) for gliding feel
    eagleTilt: {
        width: '100%', height: '100%',
        animation: 'eagleBank 15s ease-in-out forwards',
        transformOrigin: 'center center',
    },
    trailField: {
        position: 'absolute', inset: 0,
    },
    trailParticle: {
        position: 'absolute',
        top: '45%', left: 0,
        width: s(10), height: s(10),
        borderRadius: '50%',
        background: 'radial-gradient(circle, #fde047 0%, rgba(201,168,76,0.6) 50%, transparent 100%)',
        opacity: 0,
        animation: 'eagleTrail 3.5s ease-out forwards',
    },
    burstField: {
        position: 'absolute',
        top: '50%', left: '50%',
        width: 0, height: 0,
    },
    burst: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(14), height: s(14),
        borderRadius: '50%',
        background: 'radial-gradient(circle, #fff 0%, #fde047 40%, transparent 75%)',
        opacity: 0,
        animation: 'eagleBurst 1.4s ease-out forwards',
    },
};


// ── Albatross Animation ───────────────────────────────────────────────────────

function AlbatrossAnimation({ event }: { event: ScoringEventDto }) {
    return (
        <div style={albatrossStyles.scene}>
            {/* Vignette + purple wash */}
            <div style={albatrossStyles.vignette} />
            <div style={albatrossStyles.bgWash} />

            {/* Ambient drifting particles */}
            <div style={albatrossStyles.particleField}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => (
                    <div key={i} style={{
                        ...albatrossStyles.driftParticle,
                        left: `${(i * 8.3) % 100}%`,
                        animationDelay: `${i * 0.8}s`,
                        animationDuration: `${14 + (i % 5)}s`,
                    }} />
                ))}
            </div>

            {/* Background flock — two albatrosses */}
            <div style={{ ...albatrossStyles.flockBird, ...albatrossStyles.flockBirdB }}>
                <AlbatrossSvg size={130} />
            </div>
            <div style={{ ...albatrossStyles.flockBird, ...albatrossStyles.flockBirdC }}>
                <AlbatrossSvg size={110} />
            </div>

            {/* Lead albatross — circles and hovers over card */}
            <div style={albatrossStyles.leadFlight}>
                <div style={albatrossStyles.leadBank}>
                    <AlbatrossSvg size={200} />
                </div>
            </div>

            {/* Card — true centre, royal purple + gold */}
            <div style={albatrossStyles.card}>
                <div style={albatrossStyles.halo} />
                <div style={albatrossStyles.cardInner}>
                    <div style={albatrossStyles.label}>ALBATROSS!</div>
                    <div style={albatrossStyles.player}>{event.playerName}</div>
                    <div style={albatrossStyles.hole}>{event.holeName}</div>
                    <div style={albatrossStyles.subText}>3 under par</div>
                </div>
            </div>

            {/* Big starburst when lead albatross passes overhead */}
            <div style={albatrossStyles.burstField}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(i => {
                    const angle = (i / 14) * Math.PI * 2;
                    const dx = Math.round(Math.cos(angle) * 140);
                    const dy = Math.round(Math.sin(angle) * 140);
                    const isGold = i % 2 === 0;
                    return (
                        <div key={i} style={{
                            ...albatrossStyles.burst,
                            background: isGold
                                ? 'radial-gradient(circle, #fff 0%, #fde047 40%, transparent 75%)'
                                : 'radial-gradient(circle, #fff 0%, #c4b5fd 40%, transparent 75%)',
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${15 + i * 0.04}s`,
                        } as React.CSSProperties} />
                    );
                })}
            </div>
        </div>
    );
}

function AlbatrossSvg({ size }: { size: number }) {
    const w = s(size);
    const h = s(size * 0.55);
    return (
        <svg width={w} height={h} viewBox="0 0 200 110" style={{ display: 'block' }}>
            {/* Tail */}
            <polygon points="100,68 88,62 92,80 102,74" fill="#1f2937" />

            {/* Body */}
            <ellipse cx="100" cy="58" rx="20" ry="9" fill="#f9fafb" />
            <ellipse cx="100" cy="62" rx="16" ry="6" fill="#e5e7eb" />

            {/* Left wing — long albatross silhouette */}
            <path d="M 92 54 Q 60 28 20 38 Q 8 42 4 48 Q 30 50 60 54 Q 80 56 92 58 Z"
                fill="#f9fafb" />
            {/* Left wing dark tip */}
            <path d="M 4 48 Q 14 42 28 40 Q 18 48 4 50 Z" fill="#1f2937" />
            <path d="M 30 42 Q 40 40 50 42 Q 42 48 32 48 Z" fill="#374151" opacity="0.85" />
            {/* Wing trailing edge shading */}
            <path d="M 60 56 Q 80 58 92 58 L 92 60 Q 75 60 60 58 Z" fill="#d1d5db" opacity="0.7" />

            {/* Right wing */}
            <path d="M 108 54 Q 140 28 180 38 Q 192 42 196 48 Q 170 50 140 54 Q 120 56 108 58 Z"
                fill="#f9fafb" />
            {/* Right wing dark tip */}
            <path d="M 196 48 Q 186 42 172 40 Q 182 48 196 50 Z" fill="#1f2937" />
            <path d="M 170 42 Q 160 40 150 42 Q 158 48 168 48 Z" fill="#374151" opacity="0.85" />
            <path d="M 140 56 Q 120 58 108 58 L 108 60 Q 125 60 140 58 Z" fill="#d1d5db" opacity="0.7" />

            {/* Head */}
            <ellipse cx="124" cy="54" rx="9" ry="7" fill="#f9fafb" />

            {/* Eye */}
            <circle cx="128" cy="53" r="1.8" fill="#0a0a0a" />

            {/* Beak — long, pinkish-yellow albatross beak */}
            <path d="M 132 54 Q 144 54 142 58 Q 138 56 132 56 Z" fill="#fde047" />
            <path d="M 132 54 Q 144 54 142 58 Q 138 56 132 56 Z" fill="none" stroke="#c9a84c" strokeWidth="0.5" />
        </svg>
    );
}

const albatrossStyles: Record<string, React.CSSProperties> = {
    scene: {
        position: 'absolute', inset: 0, zIndex: 170,
        pointerEvents: 'none',
    },
    vignette: {
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.45) 75%, rgba(0,0,0,0.65) 100%)',
        animation: 'albVignette 30s ease-in-out forwards',
    },
    bgWash: {
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.22) 0%, rgba(99,49,222,0.12) 35%, transparent 70%)',
        animation: 'albBgWash 30s ease-in-out forwards',
    },
    particleField: {
        position: 'absolute', inset: 0,
        overflow: 'hidden',
    },
    driftParticle: {
        position: 'absolute',
        bottom: '-20px',
        width: s(6), height: s(6),
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(253,224,71,0.7) 0%, rgba(196,181,253,0.4) 50%, transparent 100%)',
        animation: 'albDrift 14s linear infinite',
    },

    // Background flock — two smaller albatrosses crossing upper third
    flockBird: {
        position: 'absolute',
        top: 0, left: 0,
        zIndex: 2,
    },
    flockBirdB: {
        animation: 'albFlockB 30s ease-in-out forwards',
    },
    flockBirdC: {
        animation: 'albFlockC 30s ease-in-out forwards',
    },

    // Lead bird — sweeps in, hovers over card, glides off
    leadFlight: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(200), height: s(110),
        zIndex: 3,
        animation: 'albLeadPath 30s cubic-bezier(0.45, 0, 0.55, 1) forwards',
    },
    leadBank: {
        width: '100%', height: '100%',
        animation: 'albLeadBank 30s ease-in-out forwards',
        transformOrigin: 'center center',
    },

    // Card — true centre of screen
    card: {
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        animation: 'albCardIn 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    halo: {
        position: 'absolute', inset: `-${s(40)}px`,
        borderRadius: s(40),
        background: 'radial-gradient(ellipse at center, rgba(253,224,71,0.35) 0%, rgba(139,92,246,0.25) 40%, transparent 75%)',
        animation: 'albHalo 30s ease-in-out infinite',
        pointerEvents: 'none',
    },
    cardInner: {
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.95), rgba(99,49,222,0.95))',
        border: `${s(6)}px solid #fde047`,
        borderRadius: s(24),
        padding: `${s(60)}px ${s(96)}px`,
        boxShadow: '0 24px 64px rgba(0,0,0,0.8), 0 0 100px rgba(253,224,71,0.5)',
        minWidth: s(800),
        textAlign: 'center' as const,
    },
    label: {
        fontSize: s(72), fontWeight: 900, color: '#fde047', letterSpacing: 16,
        marginBottom: s(32), textTransform: 'uppercase' as const,
        textShadow: '0 6px 20px rgba(0,0,0,0.6)',
    },
    player: {
        fontSize: s(80), fontWeight: 900, color: '#fff',
        marginBottom: s(12), textShadow: '0 6px 20px rgba(0,0,0,0.6)',
    },
    hole: {
        fontSize: s(36), color: 'rgba(255,255,255,0.95)', fontWeight: 700,
    },
    subText: {
        fontSize: s(20), color: 'rgba(255,255,255,0.9)', letterSpacing: 4,
        textTransform: 'uppercase' as const, marginTop: s(16), fontWeight: 600,
    },

    burstField: {
        position: 'absolute',
        top: '32%', left: '50%',
        width: 0, height: 0,
    },
    burst: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(18), height: s(18),
        borderRadius: '50%',
        opacity: 0,
        animation: 'albBurst 2s ease-out forwards',
    },
};


// ── Hole In One Animation ─────────────────────────────────────────────────────

function FireworkBurst({ x, delay, colour }: { x: number; delay: number; colour: string }) {
    const sparks: { dx: number; dy: number; i: number }[] = [];
    for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2;
        const dist = 120 + (i % 3) * 30;
        sparks.push({
            dx: Math.round(Math.cos(angle) * dist),
            dy: Math.round(Math.sin(angle) * dist),
            i,
        });
    }
    // Unique animation name per burst so sparks animate to different end points
    const animPrefix = `hioSpk_${Math.round(x)}_${Math.round(delay)}`;

    const keyframesCss = sparks.map(sp => `
        @keyframes ${animPrefix}_${sp.i} {
            0%   { opacity: 0; transform: translate(0, -60vh) scale(0.3); }
            10%  { opacity: 1; transform: translate(${Math.round(sp.dx * 0.5)}px, calc(-60vh + ${Math.round(sp.dy * 0.5)}px)) scale(1); }
            60%  { opacity: 1; transform: translate(${sp.dx}px, calc(-60vh + ${sp.dy}px)) scale(0.9); }
            100% { opacity: 0; transform: translate(${sp.dx}px, calc(-60vh + ${sp.dy + 80}px)) scale(0.3); }
        }
    `).join('');

    return (
        <>
            <style>{keyframesCss}</style>
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: `${x}%`,
                width: 0, height: 0,
                pointerEvents: 'none',
            }}>
                <div style={{
                    position: 'absolute',
                    bottom: 0, left: 0,
                    width: s(4), height: s(14),
                    background: `linear-gradient(to bottom, ${colour}, transparent)`,
                    borderRadius: s(2),
                    opacity: 0,
                    animation: `hioFireworkRise 1.2s ${delay}s ease-out forwards`,
                }} />
                {sparks.map(sp => (
                    <div key={sp.i} style={{
                        position: 'absolute',
                        bottom: 0, left: 0,
                        width: s(6), height: s(6),
                        borderRadius: '50%',
                        background: `radial-gradient(circle, #fff 0%, ${colour} 50%, transparent 100%)`,
                        opacity: 0,
                        animation: `${animPrefix}_${sp.i} 2.2s ${delay + 1.2}s ease-out forwards`,
                    }} />
                ))}
            </div>
        </>
    );
}


function HoleInOneAnimation({ event }: { event: ScoringEventDto }) {
    return (
        <div style={hioStyles.scene}>
            {/* Pastel sky gradient */}
            <div style={hioStyles.sky} />

            {/* Sun with halo */}
            <div style={hioStyles.sunHalo} />
            <div style={hioStyles.sun} />

            {/* Distant clouds drifting */}
            <div style={hioStyles.cloudLayer} />

            {/* Rolling green hill */}
            <div style={hioStyles.hill} />
            <div style={hioStyles.hillFront} />

            {/* The flag, far right on the hill */}
            <div style={hioStyles.flagWrap}>
                <FlagSvg />
            </div>

            {/* The hole — a dark ellipse on the green near the flag */}
            <div style={hioStyles.hole} />

            {/* The tee, bottom-left */}
            <div style={hioStyles.teeWrap}>
                <TeeSvg />
            </div>

            {/* Swing line flash */}
            <div style={hioStyles.swingFlash} />

            {/* Impact poof at tee */}
            <div style={hioStyles.teePoof}>
                {[0, 1, 2, 3, 4, 5].map(i => {
                    const angle = (i / 6) * Math.PI - Math.PI / 2;
                    const dx = Math.round(Math.cos(angle) * 50);
                    const dy = Math.round(Math.sin(angle) * 50);
                    return (
                        <div key={i} style={{
                            ...hioStyles.poofParticle,
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${4.5 + i * 0.02}s`,
                        } as React.CSSProperties} />
                    );
                })}
            </div>

            {/* The ball — flies the whole journey */}
            <div style={hioStyles.ballFlight}>
                <div style={hioStyles.ballVertical}>
                    <div style={hioStyles.ballSpin}>
                        <BallSvg />
                    </div>
                </div>
            </div>

            {/* Motion trail behind the ball */}
            <div style={hioStyles.trailField}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} style={{
                        ...hioStyles.trailDot,
                        animationDelay: `${4.6 + i * 0.08}s`,
                    }} />
                ))}
            </div>

            {/* Grass-poof particles when ball lands */}
            <div style={hioStyles.landingPoof}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
                    const angle = (i / 8) * Math.PI * 2;
                    const dx = Math.round(Math.cos(angle) * 35);
                    const dy = Math.round(Math.sin(angle) * 20);
                    return (
                        <div key={i} style={{
                            ...hioStyles.poofParticle,
                            background: 'radial-gradient(circle, rgba(180,220,140,0.9) 0%, transparent 70%)',
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${8.1 + i * 0.03}s`,
                        } as React.CSSProperties} />
                    );
                })}
            </div>

            {/* Drop "clink" — small flash at the hole */}
            <div style={hioStyles.dropFlash} />

            {/* ACE stamp + card */}
            <div style={hioStyles.aceBanner}>
                <div style={hioStyles.aceText}>ACE!</div>
                <div style={hioStyles.acePlayer}>{event.playerName}</div>
                <div style={hioStyles.aceHole}>{event.holeName}</div>
                <div style={hioStyles.aceSub}>HOLE IN ONE</div>
            </div>

            {/* Massive starburst at the moment ACE stamps */}
            <div style={hioStyles.aceBurst}>
                {[...Array(20)].map((_, i) => {
                    const angle = (i / 20) * Math.PI * 2;
                    const dist = 180 + (i % 3) * 40;
                    const dx = Math.round(Math.cos(angle) * dist);
                    const dy = Math.round(Math.sin(angle) * dist);
                    const isGold = i % 2 === 0;
                    return (
                        <div key={i} style={{
                            ...hioStyles.burstParticle,
                            background: isGold
                                ? 'radial-gradient(circle, #fff 0%, #fde047 40%, transparent 75%)'
                                : 'radial-gradient(circle, #fff 0%, #ffb84d 40%, transparent 75%)',
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${9.5 + i * 0.025}s`,
                        } as React.CSSProperties} />
                    );
                })}
            </div>

            {/* Confetti rain */}
            <div style={hioStyles.confettiField}>
                {[...Array(40)].map((_, i) => {
                    const colours = ['#fde047', '#f59e0b', '#fb923c', '#a78bfa', '#4ade80', '#60a5fa'];
                    const colour = colours[i % colours.length];
                    return (
                        <div key={i} style={{
                            ...hioStyles.confettiPiece,
                            background: colour,
                            left: `${(i * 2.5) % 100}%`,
                            width: i % 3 === 0 ? s(8) : s(6),
                            height: i % 3 === 0 ? s(14) : s(10),
                            animationDelay: `${11 + (i * 0.08) % 6}s`,
                            animationDuration: `${5 + (i % 4)}s`,
                        }} />
                    );
                })}
            </div>

            {/* Fireworks — five bursts at different positions and times */}
            <FireworkBurst x={22} delay={20} colour="#fde047" />
            <FireworkBurst x={50} delay={23} colour="#a78bfa" />
            <FireworkBurst x={75} delay={26} colour="#60a5fa" />
            <FireworkBurst x={35} delay={30} colour="#f87171" />
            <FireworkBurst x={65} delay={33} colour="#4ade80" />


        </div>
    );
}


function TeeSvg() {
    return (
        <svg width={s(60)} height={s(80)} viewBox="0 0 60 80" style={{ display: 'block' }}>
            {/* Tee peg */}
            <rect x="26" y="40" width="8" height="38" rx="2" fill="#d4a574" />
            <ellipse cx="30" cy="40" rx="11" ry="4" fill="#c9a84c" />
            {/* Ball on tee (will be hidden when ball animation starts) */}
            <circle cx="30" cy="32" r="9" fill="#fff" />
            <circle cx="30" cy="32" r="9" fill="none" stroke="#d4d4d4" strokeWidth="0.5" />
            {/* Dimples */}
            <circle cx="27" cy="29" r="1" fill="#e5e5e5" />
            <circle cx="32" cy="30" r="1" fill="#e5e5e5" />
            <circle cx="29" cy="34" r="1" fill="#e5e5e5" />
        </svg>
    );
}

function BallSvg() {
    return (
        <svg width={s(28)} height={s(28)} viewBox="0 0 28 28" style={{ display: 'block' }}>
            <circle cx="14" cy="14" r="13" fill="#fff" />
            <circle cx="14" cy="14" r="13" fill="none" stroke="#cbd5e1" strokeWidth="0.8" />
            {/* Dimples */}
            <circle cx="9" cy="10" r="1.2" fill="#e5e7eb" />
            <circle cx="15" cy="9" r="1.2" fill="#e5e7eb" />
            <circle cx="19" cy="13" r="1.2" fill="#e5e7eb" />
            <circle cx="11" cy="15" r="1.2" fill="#e5e7eb" />
            <circle cx="16" cy="17" r="1.2" fill="#e5e7eb" />
            <circle cx="9" cy="18" r="1.2" fill="#e5e7eb" />
        </svg>
    );
}

function FlagSvg() {
    return (
        <svg width={s(50)} height={s(110)} viewBox="0 0 50 110" style={{ display: 'block' }}>
            {/* Flagstick */}
            <rect x="22" y="6" width="3" height="100" fill="#1f2937" />
            {/* Flag — animates via parent transform */}
            <g style={{ transformOrigin: '24px 12px', animation: 'hioFlagWave 1.6s ease-in-out infinite alternate' }}>
                <path d="M 25 6 L 46 14 L 46 24 L 25 32 Z" fill="#dc2626" />
                <path d="M 25 6 L 46 14 L 46 18 L 25 14 Z" fill="#ef4444" />
            </g>
            {/* Base of pin */}
            <ellipse cx="23.5" cy="106" rx="6" ry="2" fill="#374151" opacity="0.6" />
        </svg>
    );
}

const hioStyles: Record<string, React.CSSProperties> = {
    scene: {
        position: 'absolute', inset: 0, zIndex: 180,
        pointerEvents: 'none',
        overflow: 'hidden',
        animation: 'hioSceneFade 45s ease-in-out forwards',
    },
    sky: {
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, #93c5fd 0%, #bae6fd 30%, #e0f2fe 55%, #fef3c7 75%, #fed7aa 95%, #fdba74 100%)',
    },
    sun: {
        position: 'absolute',
        top: '12%', right: '14%',
        width: s(120), height: s(120),
        borderRadius: '50%',
        background: 'radial-gradient(circle, #fef3c7 0%, #fde047 50%, #fbbf24 85%, transparent 100%)',
        boxShadow: '0 0 60px 20px rgba(253, 224, 71, 0.4)',
        animation: 'hioSunPulse 6s ease-in-out infinite',
        zIndex: 1,
    },
    sunHalo: {
        position: 'absolute',
        top: '8%', right: '10%',
        width: s(180), height: s(180),
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(253, 224, 71, 0.3) 0%, rgba(251, 191, 36, 0.15) 40%, transparent 75%)',
        animation: 'hioSunPulse 6s ease-in-out infinite',
        zIndex: 1,
    },
    cloudLayer: {
        position: 'absolute', inset: 0,
        overflow: 'hidden',
    },
    cloud: {
        position: 'absolute',
        width: s(140), height: s(40),
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.3) 60%, transparent 100%)',
        animation: 'hioCloudDrift 45s linear infinite',
    },
    hill: {
        position: 'absolute',
        bottom: 0, left: '-10%', right: '-10%',
        height: '40%',
        background: 'linear-gradient(to bottom, #86efac 0%, #4ade80 40%, #16a34a 100%)',
        borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
        animation: 'hioHillRise 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        transform: 'translateY(100%)',
    },
    hillFront: {
        position: 'absolute',
        bottom: 0, left: '-15%', right: '-15%',
        height: '20%',
        background: 'linear-gradient(to bottom, #4ade80 0%, #15803d 100%)',
        borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
        animation: 'hioHillRise 1.5s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        transform: 'translateY(100%)',
    },
    teeWrap: {
        position: 'absolute',
        bottom: '18%', left: '8%',
        opacity: 0,
        animation: 'hioTeeShow 0.8s 1.8s ease-out forwards, hioBallVanish 0.1s 4.4s forwards',
    },
    flagWrap: {
        position: 'absolute',
        // Anchor at 15% from right, then translate so the flagstick (at SVG x=23.5
        // in a 50-wide viewBox = 47% from left) sits at that anchor point.
        bottom: '20%', right: '15%',
        transform: 'translateX(47%)',
        opacity: 0,
        animation: 'hioFlagShow 0.8s 2s ease-out forwards',
    },
    hole: {
        position: 'absolute',
        // Anchor at 15% from right, translate left by half its own width to centre it.
        bottom: '19%', right: '15%',
        transform: 'translateX(50%)',
        width: s(40), height: s(14),
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, #1a1a1a 0%, #0a0a0a 70%, transparent 100%)',
        opacity: 0,
        animation: 'hioFlagShow 0.8s 2.2s ease-out forwards',
    },
    swingFlash: {
        position: 'absolute',
        bottom: '22%', left: '5%',
        width: s(120), height: s(4),
        background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9), transparent)',
        transform: 'rotate(-45deg)',
        opacity: 0,
        animation: 'hioSwingFlash 0.3s 4.3s ease-out forwards',
    },
    teePoof: {
        position: 'absolute',
        bottom: '20%', left: '10%',
        width: 0, height: 0,
    },
    ballFlight: {
        // outer: horizontal travel left-to-right at linear pace
        position: 'absolute',
        bottom: 0, left: 0,
        width: s(28), height: s(28),
        opacity: 0,
        animation: 'hioBallShow 0.1s 4.5s forwards, hioBallX 7s 4.5s linear forwards',
    },
    ballVertical: {
        // inner wrapper: vertical movement, separately curved
        width: '100%', height: '100%',
        animation: 'hioBallY 7s 4.5s forwards',
    },
    ballSpin: {
        width: '100%', height: '100%',
        animation: 'hioBallSpin 0.4s 4.5s linear infinite',
    },
    trailField: {
        position: 'absolute', inset: 0,
    },
    trailDot: {
        position: 'absolute',
        bottom: 0, left: 0,
        width: s(10), height: s(10),
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
        opacity: 0,
        animation: 'hioTrailX 7s linear forwards, hioTrailY 7s forwards, hioTrailFade 7s forwards',
    },
    landingPoof: {
        position: 'absolute',
        bottom: '22%', right: '17%',
        width: 0, height: 0,
    },
    dropFlash: {
        position: 'absolute',
        bottom: '20%', right: '15%',
        marginRight: s(-30),
        width: s(60), height: s(30),
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(253,224,71,0.95) 0%, rgba(255,255,255,0.6) 40%, transparent 100%)',
        opacity: 0,
        animation: 'hioDropFlash 0.6s 8.55s ease-out forwards',
    },
    aceBanner: {
        position: 'absolute',
        top: '38%', left: '50%',
        transform: 'translate(-50%, -50%) scale(0)',
        textAlign: 'center' as const,
        opacity: 0,
        animation: 'hioAceStamp 0.6s 9.5s cubic-bezier(0.34, 1.7, 0.5, 1) forwards, hioAceHold 35s 10s ease-in-out forwards',
        zIndex: 5,
    },
    aceText: {
        fontSize: s(180),
        fontWeight: 900,
        color: '#fff',
        letterSpacing: s(8),
        textShadow: '0 8px 32px rgba(220,38,38,0.8), 0 4px 12px rgba(0,0,0,0.6), 0 0 80px rgba(253,224,71,0.9)',
        lineHeight: 1,
        marginBottom: s(8),
        WebkitTextStroke: '4px #dc2626',
    },
    aceSub: {
        fontSize: s(28),
        fontWeight: 900,
        color: '#fde047',
        letterSpacing: s(12),
        textTransform: 'uppercase' as const,
        textShadow: '0 4px 12px rgba(0,0,0,0.7)',
        marginTop: s(16),
    },
    acePlayer: {
        fontSize: s(56),
        fontWeight: 800,
        color: '#fff',
        textShadow: '0 4px 16px rgba(0,0,0,0.7)',
        marginTop: s(12),
    },
    aceHole: {
        fontSize: s(32),
        fontWeight: 600,
        color: 'rgba(255,255,255,0.95)',
        textShadow: '0 3px 12px rgba(0,0,0,0.6)',
        marginTop: s(4),
    },
    aceBurst: {
        position: 'absolute',
        top: '38%', left: '50%',
        width: 0, height: 0,
        zIndex: 4,
    },
    burstParticle: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(18), height: s(18),
        borderRadius: '50%',
        opacity: 0,
        animation: 'hioBurst 2s ease-out forwards',
    },
    poofParticle: {
        position: 'absolute',
        top: 0, left: 0,
        width: s(12), height: s(12),
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(229,231,235,0.5) 50%, transparent 100%)',
        opacity: 0,
        animation: 'hioPoof 0.8s ease-out forwards',
    },
    confettiField: {
        position: 'absolute', inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
    },
    confettiPiece: {
        position: 'absolute',
        top: '-20px',
        animation: 'hioConfetti 6s ease-in forwards',
        opacity: 0,
    },
};

// ── Scoring Celebration - Change name of function at later date ─────────────────────────────────────────────────────────

function EagleCelebration({ event, onDone }: {
    event: ScoringEventDto;
    onDone: () => void;
}) {
    const stableOnDone = useCallback(onDone, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isHoleInOne = event.scoreType === 'HoleInOne';
    const isAlbatross = event.scoreType === 'Albatross';
    const isEagle = event.scoreType === 'Eagle';
    const isBirdie = event.scoreType === 'Birdie';

    const displayMs = isHoleInOne ? 45000
        : isBirdie ? 10000
        : isAlbatross ? 30000
        : 15000;

    useEffect(() => {
        const timer = setTimeout(stableOnDone, displayMs);
        return () => clearTimeout(timer);
    }, [stableOnDone, displayMs]);

    if (isHoleInOne) {
        return <HoleInOneAnimation event={event} />;
    }

    if (isAlbatross) {
        return <AlbatrossAnimation event={event} />;
    }

    if (isEagle) {
        return <EagleAnimation event={event} />;
    }

    if (isBirdie) {
        return <BirdieAnimation event={event} />;
    }

    return null;
}

// ── Hole Sponsor Bar ──────────────────────────────────────────────────────────

function CameraSponsorBar({ sponsor }: { sponsor: CameraSponsorView }) {
    return (
        <div style={holeSponsorStyles.bar}>
            <span style={holeSponsorStyles.label}>Hole Proudly Sponsored By</span>
            {sponsor.logoUrl ? (
                <img
                    src={sponsor.logoUrl}
                    alt={sponsor.sponsorName}
                    style={holeSponsorStyles.logo}
                    onError={e => {
                        const el = e.target as HTMLImageElement;
                        el.style.display = 'none';
                        const sibling = el.nextSibling as HTMLElement | null;
                        if (sibling) sibling.style.display = 'block';
                    }}
                />
            ) : null}
            <span style={{
                ...holeSponsorStyles.name,
                display: sponsor.logoUrl ? 'none' : 'block',
            }}>
                {sponsor.sponsorName}
            </span>
        </div>
    );
}

const holeSponsorStyles: Record<string, React.CSSProperties> = {
    bar: {
        position: 'absolute', bottom: s(80), left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
        borderRadius: s(8), padding: `${s(8)}px ${s(20)}px`, display: 'flex', alignItems: 'center',
        gap: s(12), zIndex: 95, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        animation: 'fadeIn 0.5s ease',
    },
    label: {
        fontSize: s(11), fontWeight: 600, color: 'rgba(0,0,0,0.45)',
        letterSpacing: 0.8, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const,
    },
    logo: { maxHeight: s(32), maxWidth: s(120), objectFit: 'contain' as const },
    name: { fontSize: s(15), fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' as const },
};

// ── Ticker Bar ────────────────────────────────────────────────────────────────

function TickerBar({ config, onEagle, onBirdie }: {
    config: DisplayConfig;
    onEagle: (event: ScoringEventDto) => void;
    onBirdie: (event: ScoringEventDto) => void;
}) {
    const comp     = config.activeCompetition;
    const settings = comp?.displaySettings;

    const eagleFiredRef      = useRef<Set<string>>(new Set());
    const markingRef         = useRef<Set<string>>(new Set());
    const transitioningRef   = useRef(false);
    const minDisplayUntilRef = useRef<number>(0);
    const [unseenEvents, setUnseenEvents] = useState<ScoringEventDto[]>([]);
    const [currentEvent, setCurrentEvent] = useState<ScoringEventDto | null>(null);
    const [eventVisible, setEventVisible] = useState(false);

    const primarySponsor = comp?.sponsors?.[0] ?? config.sponsors?.[0] ?? null;

    useEffect(() => {
        if (!comp || comp.isAdhoc || !comp.isStreamingWindow) return;
        const eligible = comp.recentScoringEvents.filter(e => {
            if (e.scoreType === 'HoleInOne') return true;  // always show — trumps all settings
            if (e.scoreType === 'Eagle' || e.scoreType === 'Albatross') return settings?.showEagles ?? true;
            if (e.scoreType === 'Birdie') return settings?.showBirdies ?? true;
            if (e.scoreType === 'Par') return settings?.showPars ?? true;
            return false;
        });
        if (eligible.length > 0) {
            setUnseenEvents(prev => {
                const existingIds = new Set(prev.map(e => e.id));
                if (currentEvent) existingIds.add(currentEvent.id);
                const toAdd = eligible.filter(e => !existingIds.has(e.id));
                return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    const markDisplayed = useCallback((eventId: string) => {
        if (markingRef.current.has(eventId)) return;
        markingRef.current.add(eventId);
        setTimeout(async () => {
            try {
                await fetch(`${API_BASE}/api/display/${CLUB_CODE}/events/${eventId}/displayed`, { method: 'POST' });
            } catch {
                markingRef.current.delete(eventId);
            }
        }, 10000);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (unseenEvents.length === 0) return;
        if (transitioningRef.current) return;
        const now = Date.now();
        if (now < minDisplayUntilRef.current) {
            const delay = minDisplayUntilRef.current - now;
            const timer = setTimeout(() => { setUnseenEvents(prev => [...prev]); }, delay);
            return () => clearTimeout(timer);
        }
        const next = unseenEvents[0];
        transitioningRef.current = true;
        const showNext = () => {
            // Celebration durations: HoleInOne 45s, Albatross 30s, Eagle 15s, Birdie 10s, others 10s
            const celebrationMs =
                next.scoreType === 'HoleInOne' ? 46000 :
                next.scoreType === 'Albatross' ? 31000 :
                next.scoreType === 'Eagle' ? 16000 :
                next.scoreType === 'Birdie' ? 11000 :
                10000;
            minDisplayUntilRef.current = Date.now() + celebrationMs;
            setCurrentEvent(next);
            setUnseenEvents(prev => prev.slice(1));
            requestAnimationFrame(() => {
                setTimeout(() => { setEventVisible(true); transitioningRef.current = false; }, 30);
            });
            markDisplayed(next.id);
            if (!eagleFiredRef.current.has(next.id)) {
                eagleFiredRef.current.add(next.id);
                if (next.scoreType === 'HoleInOne' ||
                    next.scoreType === 'Eagle' ||
                    next.scoreType === 'Albatross') onEagle(next);
                else if (next.scoreType === 'Birdie') onBirdie(next);
            }
        };
        if (currentEvent) { setEventVisible(false); setTimeout(showNext, 900); }
        else { showNext(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unseenEvents]);

    const getBadge = (scoreType: string) => {
        switch (scoreType) {
            case 'HoleInOne': return { text: 'HOLE IN ONE!', bg: '#fde047', color: '#000' };
            case 'Eagle': case 'Albatross': return { text: scoreType.toUpperCase(), bg: '#ffd700', color: '#000' };
            case 'Birdie': return { text: 'BIRDIE', bg: '#4ade80', color: '#000' };
            case 'Par': return { text: 'PAR', bg: 'rgba(255,255,255,0.2)', color: '#fff' };
            default: return null;
        }
    };

    const badge = currentEvent ? getBadge(currentEvent.scoreType) : null;

    return (
        <div style={tickerStyles.bar}>
            <div style={tickerStyles.label}>{config.clubName.toUpperCase()}</div>
            <div style={tickerStyles.centre}>
                {currentEvent && (
                    <div style={{
                        ...tickerStyles.eventSlide,
                        opacity:   eventVisible ? 1 : 0,
                        transform: eventVisible ? 'translateX(0)' : 'translateX(100vw)',
                    }}>
                        {badge && (
                            <span style={{ ...tickerStyles.badge, background: badge.bg, color: badge.color }}>
                                {badge.text}
                            </span>
                        )}
                        <span style={tickerStyles.eventText}>
                            {currentEvent.playerName}
                            <span style={tickerStyles.eventSep}> — </span>
                            {currentEvent.scoreType === 'HoleInOne' ? 'Hole In One' : currentEvent.scoreType} on {currentEvent.holeName}
                        </span>
                    </div>
                )}
            </div>
            {primarySponsor && (
                <div style={tickerStyles.sponsorPanel}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: s(2) }}>
                        <span style={tickerStyles.sponsoredBy}>Kindly Sponsored By</span>
                        <span style={tickerStyles.sponsorName}>{primarySponsor.name}</span>
                    </div>
                    {primarySponsor.logoUrl ? (
                        <img src={primarySponsor.logoUrl} alt={primarySponsor.name}
                            style={tickerStyles.sponsorLogo}
                            onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                    ) : null}
                </div>
            )}
        </div>
    );
}

const tickerStyles: Record<string, React.CSSProperties> = {
    bar: {
        position: 'absolute', bottom: 0, left: 0, right: 0, height: s(72),
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', zIndex: 100, overflow: 'hidden',
    },
    label: {
        background: '#1a6b3c', color: '#fff', fontSize: s(16), fontWeight: 800,
        letterSpacing: 1.5, padding: `0 ${s(24)}px`, height: '100%',
        display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0,
    },
    centre: { flex: 1, display: 'flex', alignItems: 'center', padding: `0 ${s(24)}px`, overflow: 'hidden', minWidth: 0 },
    eventSlide: {
        display: 'flex', alignItems: 'center', gap: s(12),
        transition: 'opacity 1.0s ease-out, transform 1.0s cubic-bezier(0.16, 1, 0.3, 1)',
        whiteSpace: 'nowrap',
    },
    badge: { fontSize: s(15), fontWeight: 800, padding: `${s(5)}px ${s(16)}px`, borderRadius: s(4), letterSpacing: 1, flexShrink: 0 },
    eventText: { color: '#fff', fontSize: s(20), fontWeight: 700, lineHeight: 1 },
    eventSep: { color: 'rgba(255,255,255,0.5)', fontWeight: 400 },
    sponsorPanel: {
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: s(16),
        padding: `0 ${s(20)}px`, flexShrink: 0, background: '#fff', height: '100%',
    },
    sponsoredBy: { fontSize: s(11), fontWeight: 600, color: 'rgba(0,0,0,0.45)', letterSpacing: 0.8, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' },
    sponsorLogo: { maxHeight: s(48), maxWidth: s(140), objectFit: 'contain' as const },
    sponsorName: { fontSize: s(15), fontWeight: 700, color: '#1a6b3c', whiteSpace: 'nowrap' },
};

// ── Leaderboard ───────────────────────────────────────────────────────────────

function MiniLeaderboard({ config }: { config: DisplayConfig }) {
    const comp = config.activeCompetition;
    if (!comp || comp.isAdhoc) return null;
    const entries = comp.leaderboard.slice(0, 8);
    if (entries.length === 0) return null;

    return (
        <div style={miniLbStyles.panel}>
            <div style={miniLbStyles.header}>
                <div style={miniLbStyles.compName}>{comp.name}</div>
            </div>
            {entries.map((e: LeaderboardEntryDto, i: number) => (
                <div key={e.position} style={{
                    ...miniLbStyles.row,
                    background: i === 0 ? 'rgba(255,215,0,0.15)' : i % 2 === 0 ? 'rgba(255,255,255,0.04)' : 'transparent',
                }}>
                    <span style={miniLbStyles.pos}>{e.position}</span>
                    <span style={miniLbStyles.name}>
                        {e.playerName.split(' ').map((part: string, idx: number) =>
                            idx === 0 && e.playerName.includes('.') ? part : part.toUpperCase().substring(0, 8)
                        ).join(' ')}
                    </span>
                    <span style={miniLbStyles.holes}>
                        {e.currentHole === 'F' ? 'F' : e.holesPlayed > 0 ? `${e.holesPlayed}` : '-'}
                    </span>
                    <span style={{ ...miniLbStyles.score, color: scoreColour(e.scoreToPar) }}>
                        {formatScore(e.scoreToPar, e.stablefordPoints)}
                    </span>
                </div>
            ))}
            <div style={miniLbStyles.footer}>
                Powered by <span style={{ color: '#c9a84c' }}>MiClub</span>
            </div>
        </div>
    );
}

const miniLbStyles: Record<string, React.CSSProperties> = {
    panel: {
        position: 'absolute', bottom: s(84), right: s(16), width: s(280),
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)',
        borderRadius: s(6), overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', zIndex: 90,
    },
    header: { background: 'rgba(26,107,60,0.9)', padding: `${s(10)}px ${s(14)}px` },
    compName: { color: '#fff', fontSize: s(15), fontWeight: 700, letterSpacing: 0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    round: { color: 'rgba(255,255,255,0.5)', fontSize: s(12), letterSpacing: 0.5, textTransform: 'uppercase', marginTop: s(2) },
    row: { display: 'grid', gridTemplateColumns: `${s(26)}px 1fr ${s(26)}px ${s(56)}px`, alignItems: 'center', padding: `${s(6)}px ${s(12)}px`, gap: s(4), borderBottom: '1px solid rgba(255,255,255,0.04)' },
    pos: { color: 'rgba(255,255,255,0.4)', fontSize: s(13), textAlign: 'center' },
    name: { color: '#fff', fontSize: s(14), fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    holes: { color: 'rgba(255,255,255,0.35)', fontSize: s(12), textAlign: 'center' },
    score: { fontSize: s(16), fontWeight: 700, textAlign: 'right' },
    footer: { padding: `${s(6)}px ${s(12)}px`, fontSize: s(12), color: 'rgba(255,255,255,0.25)', textAlign: 'center', letterSpacing: 0.5, textTransform: 'uppercase', borderTop: '1px solid rgba(255,255,255,0.06)' },
};

function LeaderboardPanel({ config, style, isFinalResults = false }: {
    config: DisplayConfig;
    style: 'FullScreen' | 'Overlay';
    isFinalResults?: boolean;
}) {
    const comp = config.activeCompetition;
    const pageSize = style === 'Overlay' ? 8 : 10;
    const totalEntries = comp?.leaderboard.length ?? 0;
    const totalPages = Math.ceil(totalEntries / pageSize);
    const settings = comp?.displaySettings;
    const durationMs = (settings?.leaderboardDurationSeconds ?? 30) * 1000;
    const pageMs = totalPages > 1 ? Math.floor(durationMs / totalPages) : durationMs;

    const [page, setPage] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        if (totalPages <= 1) return;
        setPage(0);
        const interval = setInterval(() => {
            setVisible(false);
            setTimeout(() => { setPage(p => (p + 1) % totalPages); setVisible(true); }, 400);
        }, pageMs);
        return () => clearInterval(interval);
    }, [totalPages, pageMs]);

    if (!comp) return null;

    const entries = comp.leaderboard.slice(page * pageSize, (page + 1) * pageSize);
    const primarySponsor = config.sponsors?.[0];

    if (style === 'Overlay') {
        return (
            <div style={lbStyles.overlayPanel}>
                <div style={lbStyles.overlayTitle}>{comp.name}</div>
                <div style={lbStyles.overlayFormat}>{comp.format}</div>
                {totalPages > 1 && (
                    <div style={lbStyles.pageIndicator}>
                        {Array.from({ length: totalPages }).map((_, i) => (
                            <div key={i} style={{ ...lbStyles.pageDot, background: i === page ? '#fff' : 'rgba(255,255,255,0.3)' }} />
                        ))}
                    </div>
                )}
                <div style={lbStyles.overlayDivider} />
                <div style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
                    {entries.map((e: LeaderboardEntryDto) => (
                        <div key={e.position} style={lbStyles.overlayRow}>
                            <span style={lbStyles.overlayPos}>{e.position}</span>
                            <span style={lbStyles.overlayName}>{e.playerName}</span>
                            <span style={lbStyles.overlayHoles}>{e.currentHole === 'F' ? 'F' : e.holesPlayed > 0 ? `H${e.holesPlayed}` : ''}</span>
                            <span style={{ ...lbStyles.overlayScore, color: scoreColour(e.scoreToPar) }}>
                                {formatScore(e.scoreToPar, e.stablefordPoints)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // ── FullScreen variant ─────────────────────────────────────────────────────
    return (
        <div style={lbStyles.fullScreen}>
            {config.heroImageUrl
                ? <div style={{ ...lbStyles.heroImage, backgroundImage: `url(${config.heroImageUrl})` }} />
                : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0a1a10 0%, #1a3a20 100%)' }} />
            }
            <div style={lbStyles.fullScreenOverlay} />
            <div style={lbStyles.fullScreenContent}>
                <div style={lbStyles.fullScreenTitle}>{comp.name}</div>
                {isFinalResults && (
                    <div style={{ color: '#c9a84c', fontSize: s(14), fontWeight: 700, textAlign: 'center', letterSpacing: 2, textTransform: 'uppercase', marginBottom: s(8) }}>
                        Final Results
                    </div>
                )}
                <div style={lbStyles.fullScreenFormat}>{comp.format} — {config.clubName}</div>
                <div style={lbStyles.poweredBy}>Powered by <span style={{ color: '#c9a84c', fontWeight: 700 }}>MiClub</span></div>
                {isFinalResults && primarySponsor && (
                    <div style={lbStyles.finalResultsSponsor}>
                        <div style={lbStyles.proudlyText}>Proudly Sponsored By</div>
                        <div style={lbStyles.sponsorDisplay}>
                            {primarySponsor.logoUrl && (
                                <img src={primarySponsor.logoUrl} alt={primarySponsor.name} style={lbStyles.sponsorLogo}
                                    onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                            )}
                            <div style={lbStyles.sponsorName}>{primarySponsor.name}</div>
                        </div>
                    </div>
                )}
                {totalEntries === 0 ? (
                    <div style={lbStyles.startingSoon}>
                        <div style={lbStyles.startingSoonTitle}>Competition Starting Soon</div>
                        <div style={lbStyles.startingSoonSub}>
                            Leaderboard will appear as scores are recorded
                        </div>
                    </div>
                ) : (
                    <>
                        {totalPages > 1 && (
                            <div style={{ ...lbStyles.pageIndicator, marginBottom: s(16) }}>
                                {Array.from({ length: totalPages }).map((_, i) => (
                                    <div key={i} style={{ ...lbStyles.pageDot, background: i === page ? '#fff' : 'rgba(255,255,255,0.3)' }} />
                                ))}
                            </div>
                        )}
                        <div style={{ ...lbStyles.fullScreenTable, opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
                            <div style={lbStyles.tableHeader}>
                                <span style={lbStyles.thPos}>POS</span>
                                <span style={lbStyles.thName}>PLAYER</span>
                                <span style={lbStyles.thHoles}>HOLES</span>
                                <span style={lbStyles.thScore}>SCORE</span>
                            </div>
                            {entries.map((e: LeaderboardEntryDto, i: number) => (
                                <div key={e.position} style={{
                                    ...lbStyles.tableRow,
                                    background: page === 0 && i === 0 ? 'rgba(255,215,0,0.15)' : i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'transparent',
                                }}>
                                    <span style={lbStyles.tdPos}>{e.position}</span>
                                    <span style={lbStyles.tdName}>{e.playerName}</span>
                                    <span style={lbStyles.tdHoles}>{e.currentHole === 'F' ? 'F' : e.holesPlayed > 0 ? `H${e.holesPlayed}` : '—'}</span>
                                    <span style={{ ...lbStyles.tdScore, color: scoreColour(e.scoreToPar) }}>
                                        {formatScore(e.scoreToPar, e.stablefordPoints)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// Inject keyframes
if (typeof document !== 'undefined' && !document.getElementById('celeb-animations')) {
    const style = document.createElement('style');
    style.id = 'celeb-animations';
    style.textContent = `
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeInScale { from { opacity: 0; transform: translateX(-50%) scale(0.7); } to { opacity: 1; transform: translateX(-50%) scale(1); } }
        @keyframes fadeInScaleBig { from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes slideInLeft { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes liveBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

        /* Birdie — full flight: bottom-left → card → idle → bottom-right */
        @keyframes birdiePath {
            0%   { transform: translate(-10vw, 90vh) scale(0.6); }
            22%  { transform: translate(35vw, 30vh) scale(0.85); }
            27%  { transform: translate(calc(50vw - 40px), 6vh) scale(1); }
            70%  { transform: translate(calc(50vw - 40px), 6vh) scale(1); }
            78%  { transform: translate(calc(60vw - 40px), 12vh) scale(0.95); }
            100% { transform: translate(110vw, 95vh) scale(0.55); }
        }
        @keyframes birdieBob {
            0%, 26%, 71%, 100% { transform: translateY(0); }
            5%, 18%  { transform: translateY(-12px); }
            12%, 23% { transform: translateY(6px); }
            40%, 60% { transform: translateY(-3px); }
            50%      { transform: translateY(3px); }
        }
        @keyframes birdieWing {
            0%   { transform: rotate(-25deg) scaleY(1); }
            100% { transform: rotate(15deg) scaleY(0.85); }
        }
        @keyframes birdieCardIn {
            from { opacity: 0; transform: translate(-50%, -20px); }
            to   { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes birdieSparkle {
            0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
            30%  { opacity: 1; transform: translate(var(--sx, 0), -20px) scale(1); }
            100% { opacity: 0; transform: translate(var(--sx, 0), -50px) scale(0.5); }
        }

        /* Eagle — wide soaring arc, slow majestic gliding */
        @keyframes eaglePath {
            0%   { transform: translate(-15vw, 25vh) scale(0.35); }
            15%  { transform: translate(8vw, 32vh) scale(0.55); }
            30%  { transform: translate(28vw, 42vh) scale(0.85); }
            40%  { transform: translate(calc(50vw - 80px), 50vh) scale(1.1); }
            55%  { transform: translate(calc(50vw - 80px), 50vh) scale(1.1); }
            70%  { transform: translate(calc(70vw - 80px), 42vh) scale(0.95); }
            85%  { transform: translate(95vw, 28vh) scale(0.6); }
            100% { transform: translate(120vw, 18vh) scale(0.3); }
        }
        @keyframes eagleBank {
            0%, 100%  { transform: rotate(-6deg); }
            10%       { transform: rotate(-3deg); }
            40%, 55%  { transform: rotate(0deg) translateY(-4px); }
            48%       { transform: rotate(0deg) translateY(2px); }
            70%       { transform: rotate(6deg); }
            85%       { transform: rotate(10deg); }
        }
        @keyframes eagleCardIn {
            from { opacity: 0; transform: translate(-50%, -30px) scale(0.85); }
            to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
        @keyframes eagleHalo {
            0%, 100% { opacity: 0.3; transform: scale(0.95); }
            35%, 55% { opacity: 1; transform: scale(1.15); }
        }
        @keyframes eagleBgGlow {
            0%, 100% { opacity: 0; }
            30%, 60% { opacity: 1; }
        }
        @keyframes eagleTrail {
            0%   { opacity: 0; transform: translate(-30vw, 0) scale(0.3); }
            20%  { opacity: 1; transform: translate(0vw, -8px) scale(1); }
            100% { opacity: 0; transform: translate(40vw, -20px) scale(0.4); }
        }
        @keyframes eagleBurst {
            0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
            30%  { opacity: 1; transform: translate(var(--dx, 0), var(--dy, 0)) scale(1); }
            100% { opacity: 0; transform: translate(calc(var(--dx, 0) * 1.5), calc(var(--dy, 0) * 1.5)) scale(0.4); }
        }

        /* Albatross — formation flyover, lead circles back over card */
        @keyframes albVignette {
            0%, 100% { opacity: 0; }
            8%, 92%  { opacity: 1; }
        }
        @keyframes albBgWash {
            0%, 100%  { opacity: 0; }
            15%, 85%  { opacity: 1; }
            45%, 55%  { opacity: 1; }
        }
        @keyframes albCardIn {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes albHalo {
            0%, 100% { opacity: 0.4; transform: scale(0.95); }
            45%, 55% { opacity: 1; transform: scale(1.15); }
        }
        @keyframes albDrift {
            0%   { transform: translateY(0) translateX(0); opacity: 0; }
            10%  { opacity: 1; }
            90%  { opacity: 1; }
            100% { transform: translateY(-110vh) translateX(20px); opacity: 0; }
        }

        /* Background flock — two birds, staggered, cross upper third */
        @keyframes albFlockB {
            0%   { transform: translate(-20vw, 18vh) scale(0.55); opacity: 0; }
            10%  { opacity: 1; }
            50%  { transform: translate(60vw, 14vh) scale(0.55); opacity: 1; }
            70%  { transform: translate(105vw, 10vh) scale(0.55); opacity: 0.6; }
            100% { transform: translate(120vw, 8vh) scale(0.55); opacity: 0; }
        }
        @keyframes albFlockC {
            0%   { transform: translate(-25vw, 24vh) scale(0.45); opacity: 0; }
            15%  { opacity: 1; }
            55%  { transform: translate(65vw, 20vh) scale(0.45); opacity: 1; }
            75%  { transform: translate(108vw, 16vh) scale(0.45); opacity: 0.5; }
            100% { transform: translate(125vw, 14vh) scale(0.45); opacity: 0; }
        }

        /* Lead albatross — enters with flock, circles back, hovers over card, exits */
        @keyframes albLeadPath {
            0%   { transform: translate(-25vw, 18vh) scale(0.4); }
            10%  { transform: translate(-5vw, 16vh) scale(0.6); }
            25%  { transform: translate(20vw, 18vh) scale(0.85); }
            40%  { transform: translate(60vw, 22vh) scale(0.95); }
            50%  { transform: translate(80vw, 28vh) scale(1); }
            58%  { transform: translate(70vw, 32vh) scale(1.05); }
            68%  { transform: translate(calc(50vw - 100px), 32vh) scale(1.1); }
            78%  { transform: translate(calc(50vw - 100px), 32vh) scale(1.1); }
            88%  { transform: translate(80vw, 25vh) scale(0.95); }
            100% { transform: translate(120vw, 15vh) scale(0.55); }
        }
        @keyframes albLeadBank {
            0%, 25%   { transform: rotate(-4deg); }
            40%       { transform: rotate(-2deg); }
            50%       { transform: rotate(15deg); }
            60%       { transform: rotate(30deg); }
            68%, 78%  { transform: rotate(0deg) translateY(-6px); }
            72%       { transform: rotate(0deg) translateY(2px); }
            88%       { transform: rotate(15deg); }
            100%      { transform: rotate(25deg); }
        }
        @keyframes albBurst {
            0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
            25%  { opacity: 1; transform: translate(var(--dx, 0), var(--dy, 0)) scale(1.2); }
            100% { opacity: 0; transform: translate(calc(var(--dx, 0) * 1.6), calc(var(--dy, 0) * 1.6)) scale(0.4); }
        }

        /* Hole In One — full sequence */
        @keyframes hioSceneFade {
            0%, 95%   { opacity: 1; }
            100%      { opacity: 0; }
        }
        @keyframes hioCloudDrift {
            0%   { transform: translateX(0); }
            100% { transform: translateX(40vw); }
        }
        @keyframes hioHillRise {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
        }
        @keyframes hioTeeShow {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hioFlagShow {
            from { opacity: 0; }
            to   { opacity: 1; }
        }
        @keyframes hioSwingFlash {
            0%   { opacity: 0; transform: rotate(-45deg) scaleX(0); }
            50%  { opacity: 1; transform: rotate(-45deg) scaleX(1); }
            100% { opacity: 0; transform: rotate(-45deg) scaleX(1); }
        }
        @keyframes hioBallShow {
            from { opacity: 0; }
            to   { opacity: 1; }
        }
        @keyframes hioBallVanish {
            from { opacity: 1; }
            to   { opacity: 0; }
        }
        /* Horizontal: smooth left-to-right travel, then settles at hole */
        @keyframes hioBallX {
            0%    { transform: translateX(11vw); }
            50%   { transform: translateX(85vw); animation-timing-function: ease-out; }
            100%  { transform: translateX(85vw); }
        }
        /* Vertical: parabolic flight, then bounce-decay, then roll, then drop. */
        @keyframes hioBallY {
            0%    { transform: translateY(-19vh); opacity: 1; animation-timing-function: cubic-bezier(0.3, 0, 0.4, 1); }
            25%   { transform: translateY(-65vh); animation-timing-function: cubic-bezier(0.6, 0, 0.7, 1); }
            50%   { transform: translateY(-19vh); animation-timing-function: cubic-bezier(0.2, 0, 0.8, 0); }
            55%   { transform: translateY(-29vh); animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1); }
            58%   { transform: translateY(-19vh); opacity: 1; animation-timing-function: step-end; }
            58.01% { transform: translateY(-19vh); opacity: 0; }
            100%  { transform: translateY(-19vh); opacity: 0; }
        }
        @keyframes hioBallSpin {
            from { transform: rotate(0); }
            to   { transform: rotate(360deg); }
        }
        @keyframes hioTrailX {
            0%    { transform: translateX(11vw); }
            50%   { transform: translateX(78vw); animation-timing-function: ease-out; }
            70%   { transform: translateX(85vw); animation-timing-function: ease-out; }
            100%  { transform: translateX(87vw); }
        }
        @keyframes hioTrailY {
            0%    { transform: translateY(-19vh); animation-timing-function: cubic-bezier(0.3, 0, 0.4, 1); }
            25%   { transform: translateY(-65vh); animation-timing-function: cubic-bezier(0.6, 0, 0.7, 1); }
            50%   { transform: translateY(-19vh); }
            100%  { transform: translateY(-19vh); }
        }
        @keyframes hioTrailFade {
            0%, 100% { opacity: 0; }
            10%      { opacity: 0.8; }
            55%      { opacity: 0.4; }
            70%      { opacity: 0; }
        }
        @keyframes hioPoof {
            0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
            30%  { opacity: 1; transform: translate(var(--dx, 0), var(--dy, 0)) scale(1); }
            100% { opacity: 0; transform: translate(calc(var(--dx, 0) * 1.4), calc(var(--dy, 0) * 1.4 - 10px)) scale(0.4); }
        }
        @keyframes hioDropFlash {
            0%   { opacity: 0; transform: scale(0.4); }
            30%  { opacity: 1; transform: scale(1.4); }
            100% { opacity: 0; transform: scale(2); }
        }
        @keyframes hioFlagWave {
            from { transform: skewY(-3deg); }
            to   { transform: skewY(5deg); }
        }
        @keyframes hioAceStamp {
            0%   { opacity: 0; transform: translate(-50%, -50%) scale(0); }
            50%  { opacity: 1; transform: translate(-50%, -50%) scale(1.4); }
            70%  { transform: translate(-50%, -50%) scale(0.9); }
            85%  { transform: translate(-50%, -50%) scale(1.05); }
            100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes hioAceHold {
            0%, 100% { transform: translate(-50%, -50%) scale(1); }
            50%      { transform: translate(-50%, -50%) scale(1.03); }
        }
        @keyframes hioBurst {
            0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
            25%  { opacity: 1; transform: translate(var(--dx, 0), var(--dy, 0)) scale(1.3); }
            100% { opacity: 0; transform: translate(calc(var(--dx, 0) * 1.7), calc(var(--dy, 0) * 1.7)) scale(0.4); }
        }
        @keyframes hioConfetti {
            0%   { opacity: 0; transform: translate(0, 0) rotate(0deg); }
            10%  { opacity: 1; }
            100% { opacity: 0; transform: translate(40px, 110vh) rotate(720deg); }
        }
        /* Sun gentle pulse */
        @keyframes hioSunPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50%      { transform: scale(1.05); opacity: 0.95; }
        }
        /* Firework projectile rising from bottom */
        @keyframes hioFireworkRise {
            0%   { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-60vh); }
        }
    `;
    document.head.appendChild(style);
}

const lbStyles: Record<string, React.CSSProperties> = {
    fullScreen: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 30 },
    heroImage: { position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center' },
    fullScreenOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' },
    fullScreenContent: { position: 'relative', zIndex: 1, width: '100%', maxWidth: s(800), padding: `0 ${s(40)}px` },
    fullScreenTitle: { color: '#fff', fontSize: s(40), fontWeight: 800, textAlign: 'center', marginBottom: s(6), textShadow: '0 2px 8px rgba(0,0,0,0.5)' },
    fullScreenFormat: { color: 'rgba(255,255,255,0.6)', fontSize: s(16), textAlign: 'center', marginBottom: s(24), letterSpacing: 1, textTransform: 'uppercase' },
    fullScreenTable: { background: 'rgba(0,0,0,0.4)', borderRadius: s(12), overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' },
    tableHeader: { display: 'grid', gridTemplateColumns: `${s(48)}px 1fr ${s(80)}px ${s(100)}px`, padding: `${s(10)}px ${s(20)}px`, background: 'rgba(26,107,60,0.8)', color: 'rgba(255,255,255,0.7)', fontSize: s(11), fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' },
    tableRow: { display: 'grid', gridTemplateColumns: `${s(48)}px 1fr ${s(80)}px ${s(100)}px`, padding: `${s(12)}px ${s(20)}px`, borderBottom: '1px solid rgba(255,255,255,0.06)', transition: 'background 0.2s' },
    thPos: { textAlign: 'center' }, thName: {}, thHoles: { textAlign: 'center' }, thScore: { textAlign: 'right' },
    tdPos: { color: 'rgba(255,255,255,0.5)', fontSize: s(14), textAlign: 'center' },
    tdName: { color: '#fff', fontSize: s(16), fontWeight: 600 },
    tdHoles: { color: 'rgba(255,255,255,0.5)', fontSize: s(13), textAlign: 'center' },
    tdScore: { fontSize: s(18), fontWeight: 700, textAlign: 'right' },
    overlayPanel: { position: 'absolute', top: 0, right: 0, bottom: s(60), width: s(280), background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)', borderLeft: '1px solid rgba(255,255,255,0.1)', padding: `${s(20)}px ${s(16)}px`, zIndex: 50, display: 'flex', flexDirection: 'column', gap: s(2) },
    overlayTitle: { color: '#fff', fontSize: s(15), fontWeight: 700, marginBottom: s(2) },
    overlayFormat: { color: 'rgba(255,255,255,0.4)', fontSize: s(11), textTransform: 'uppercase', letterSpacing: 1 },
    overlayDivider: { height: 1, background: 'rgba(255,255,255,0.1)', margin: `${s(10)}px 0` },
    overlayRow: { display: 'grid', gridTemplateColumns: `${s(22)}px 1fr ${s(30)}px ${s(50)}px`, alignItems: 'center', padding: `${s(5)}px 0`, borderBottom: '1px solid rgba(255,255,255,0.05)', gap: s(4) },
    overlayPos: { color: 'rgba(255,255,255,0.4)', fontSize: s(11) },
    overlayName: { color: '#fff', fontSize: s(12), fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    overlayHoles: { color: 'rgba(255,255,255,0.35)', fontSize: s(10), textAlign: 'center' },
    overlayScore: { fontSize: s(13), fontWeight: 700, textAlign: 'right' },
    pageIndicator: { display: 'flex', gap: s(6), justifyContent: 'center', marginTop: s(8) },
    pageDot: { width: s(6), height: s(6), borderRadius: '50%', transition: 'background 0.3s' },
    poweredBy: { color: 'rgba(255,255,255,0.3)', fontSize: s(11), textAlign: 'center' as const, letterSpacing: 1, textTransform: 'uppercase' as const, marginBottom: s(16) },
    finalResultsSponsor: { textAlign: 'center' as const, margin: `${s(24)}px 0`, padding: `${s(20)}px ${s(32)}px`, background: 'rgba(201,168,76,0.15)', border: `2px solid #c9a84c`, borderRadius: s(12), maxWidth: s(600), marginLeft: 'auto', marginRight: 'auto' },
    proudlyText: { fontSize: s(13), fontWeight: 600, color: '#c9a84c', letterSpacing: 3, textTransform: 'uppercase' as const, marginBottom: s(12) },
    sponsorDisplay: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: s(16), flexWrap: 'wrap' as const },
    sponsorLogo: { maxHeight: s(60), maxWidth: s(200), objectFit: 'contain' as const },
    sponsorName: { fontSize: s(24), fontWeight: 700, color: '#fff', letterSpacing: 1 },
    startingSoon: {
        textAlign: 'center', marginTop: s(32), padding: `${s(28)}px ${s(40)}px`,
        background: 'rgba(0,0,0,0.45)', borderRadius: s(12),
        border: '1px solid rgba(255,255,255,0.12)',
    },
    startingSoonTitle: {
        fontSize: s(20), color: 'rgba(255,255,255,0.75)', letterSpacing: 2,
        textTransform: 'uppercase', fontWeight: 700, marginBottom: s(10),
    },
    startingSoonSub: {
        fontSize: s(14), color: 'rgba(255,255,255,0.35)', letterSpacing: 0.5,
    },
};

// ── No Competition / Offline Screens ──────────────────────────────────────────

function NoCompetitionScreen({ clubName }: { clubName: string }) {
    return (
        <div style={noCompStyles.root}>
            <img src="https://stream.19thview.com.au/clips/19thview-logo.png"
                alt="19thView" style={noCompStyles.logo} />
            <div style={noCompStyles.message}>No Active Competition</div>
            <div style={noCompStyles.club}>{clubName}</div>
        </div>
    );
}

function AllCamerasOfflineScreen({ clubName, hasCompetition, competitionName }: {
    clubName: string; hasCompetition: boolean; competitionName?: string;
}) {
    return (
        <div style={noCompStyles.root}>
            <img src="https://stream.19thview.com.au/clips/19thview-logo.png"
                alt="19thView" style={noCompStyles.logo} />
            {hasCompetition ? (
                <>
                    <div style={noCompStyles.message}>{competitionName}</div>
                    <div style={noCompStyles.club}>Stream starting shortly</div>
                </>
            ) : (
                <>
                    <div style={noCompStyles.message}>No Active Stream</div>
                    <div style={noCompStyles.club}>{clubName}</div>
                </>
            )}
        </div>
    );
}

const noCompStyles: Record<string, React.CSSProperties> = {
    root: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a1a10', zIndex: 10 },
    logo: { height: s(120), maxWidth: s(400), objectFit: 'contain' as const, marginBottom: s(24) },
    message: { fontSize: s(24), color: 'rgba(255,255,255,0.6)', fontWeight: 300, letterSpacing: 2, textTransform: 'uppercase', marginBottom: s(8) },
    club: { fontSize: s(16), color: 'rgba(255,255,255,0.3)', letterSpacing: 1 },
};

function StreamConnectingDots() {
    const [count, setCount] = useState(1);
    useEffect(() => {
        const interval = setInterval(() => setCount(c => c >= 7 ? 1 : c + 1), 400);
        return () => clearInterval(interval);
    }, []);
    return (
        <div style={{ display: 'flex', gap: s(10), alignItems: 'center' }}>
            {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} style={{
                    width: i < count ? s(10) : s(6), height: i < count ? s(10) : s(6),
                    borderRadius: '50%',
                    background: i < count ? '#1a6b3c' : 'rgba(255,255,255,0.1)',
                    boxShadow: i < count ? '0 0 8px rgba(26,107,60,0.6)' : 'none',
                    transition: 'all 0.3s ease', flexShrink: 0,
                }} />
            ))}
        </div>
    );
}

// ── Hole Overlay ──────────────────────────────────────────────────────────────

function HoleOverlay({ data, isFeatured }: { data: NowPlayingData | null; isFeatured: boolean }) {
    const [expanded, setExpanded] = useState(true);
    const lastKeyRef = useRef<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const displayLabel = data?.label ?? null;
    const hasHoleInfo = !!(data?.holeNumber);
    const contentKey = data ? `${data.type}-${data.label}-${data.holeNumber}` : null;

    useEffect(() => {
        if (!contentKey || !displayLabel) return;
        if (contentKey !== lastKeyRef.current) {
            lastKeyRef.current = contentKey;
            setExpanded(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setExpanded(false), 8000);
        }
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [contentKey, displayLabel]);

    if (!displayLabel || data?.type === 'idle') return null;

    if (expanded) {
        return (
            <div style={overlayStyles.lowerThird}>
                <div style={{
                    ...overlayStyles.lowerThirdInner,
                    background: isFeatured
                        ? 'linear-gradient(135deg, rgba(201,168,76,0.97), rgba(160,128,50,0.97))'
                        : 'linear-gradient(135deg, rgba(26,107,60,0.95), rgba(20,80,45,0.95))',
                    border: isFeatured ? `2px solid #fde047` : `2px solid #c9a84c`,
                    flexDirection: 'column' as const, alignItems: 'flex-start' as const, gap: s(4),
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: s(14) }}>
                        <div style={overlayStyles.labelLarge}>{displayLabel}</div>
                        {hasHoleInfo && data!.par && (
                            <div style={{ ...overlayStyles.parTextLarge, color: isFeatured ? '#000' : '#fde047' }}>
                                PAR {data!.par}
                            </div>
                        )}
                        {hasHoleInfo && data!.distanceMetres && (
                            <div style={{ ...overlayStyles.distanceText, color: isFeatured ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.85)' }}>
                                {data!.distanceMetres}m
                            </div>
                        )}
                    </div>
                    {isFeatured && (
                        <div style={{ fontSize: s(12), fontWeight: 700, color: isFeatured ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)', letterSpacing: 2, textTransform: 'uppercase' as const }}>
                            ★ Featured Hole
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            ...overlayStyles.bug,
            background: isFeatured ? 'rgba(201,168,76,0.85)' : 'rgba(0,0,0,0.7)',
            border: isFeatured ? '1px solid #fde047' : '1px solid rgba(255,255,255,0.15)',
        }}>
            <span style={{ ...overlayStyles.labelSmall, color: isFeatured ? '#000' : '#fff' }}>{displayLabel}</span>
            {hasHoleInfo && data!.par && (
                <span style={{ ...overlayStyles.parTextSmall, color: isFeatured ? 'rgba(0,0,0,0.7)' : '#fde047' }}>
                    PAR {data!.par}
                </span>
            )}
        </div>
    );
}

const overlayStyles: Record<string, React.CSSProperties> = {
    lowerThird: { position: 'absolute', bottom: s(100), left: s(40), zIndex: 90, animation: 'slideInLeft 0.5s ease' },
    lowerThirdInner: { background: 'linear-gradient(135deg, rgba(26,107,60,0.95), rgba(20,80,45,0.95))', border: `2px solid #c9a84c`, borderRadius: s(10), padding: `${s(16)}px ${s(26)}px`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: s(18) },
    labelLarge: { fontSize: s(32), fontWeight: 900, color: '#fff', letterSpacing: 2, textTransform: 'uppercase' as const, textShadow: '0 2px 8px rgba(0,0,0,0.5)' },
    parTextLarge: { fontSize: s(22), fontWeight: 800, color: '#fde047', letterSpacing: 2, textShadow: '0 2px 6px rgba(0,0,0,0.4)' },
    distanceText: { fontSize: s(22), fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: 2, textShadow: '0 2px 6px rgba(0,0,0,0.4)' },
    bug: { position: 'absolute', bottom: s(100), left: s(40), background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: s(6), padding: `${s(8)}px ${s(14)}px`, zIndex: 90, display: 'flex', alignItems: 'center', gap: s(10), animation: 'fadeIn 0.3s ease' },
    labelSmall: { fontSize: s(18), fontWeight: 800, color: '#fff', letterSpacing: 1, textTransform: 'uppercase' as const },
    parTextSmall: { fontSize: s(14), fontWeight: 700, color: '#fde047', letterSpacing: 1 },
};

// ── HLS Player ───────────────────────────────────────────────────────────────

function HlsPlayer({ url, label, onStalled, onError }: {
    url: string;
    label: string;
    onStalled?: () => void;
    onError?: () => void;
}) {
    const onStalledRef = React.useRef(onStalled);
    React.useEffect(() => { onStalledRef.current = onStalled; }, [onStalled]);
    const onErrorRef = React.useRef(onError);
    React.useEffect(() => { onErrorRef.current = onError; }, [onError]);
    const stalledTimerRef = React.useRef<number | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<any>(null);
    const lastTimeRef = useRef<number>(0);
    const lastChangeRef = useRef<number>(Date.now());

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !url) return;

        if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

        import('hls.js').then(({ default: Hls }) => {
            if (Hls.isSupported()) {
                const isLarixUrl = url.includes('/live/');
                const hls = new Hls({
                    lowLatencyMode: false,
                    liveSyncDurationCount: 3,
                    liveMaxLatencyDurationCount: 8,
                    maxBufferLength: 12,
                    maxMaxBufferLength: 24,
                    startPosition: isLarixUrl ? -1 : 0,
                });

                hlsRef.current = hls;
                hls.loadSource(url);
                hls.attachMedia(video);

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
                });

                hls.on(Hls.Events.ERROR, (_event: any, data: any) => {
                    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !data.fatal) {
                        if (!stalledTimerRef.current) {
                            stalledTimerRef.current = window.setTimeout(() => {
                                onStalledRef.current?.();
                                stalledTimerRef.current = null;
                            }, 8000);
                        }
                    } else if (data.type !== Hls.ErrorTypes.NETWORK_ERROR) {
                        if (stalledTimerRef.current) { clearTimeout(stalledTimerRef.current); stalledTimerRef.current = null; }
                    }
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad(); break;
                            case Hls.ErrorTypes.MEDIA_ERROR: hls.recoverMediaError(); break;
                            default:
                                hls.destroy();
                                onErrorRef.current?.();
                                break;
                        }
                    }
                });

            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = url;
                video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
            }
        });

        const freezeCheckInterval = setInterval(() => {
            if (!video) return;
            const currentTime = video.currentTime;
            if (currentTime === lastTimeRef.current && !video.paused) {
                const frozenDuration = Date.now() - lastChangeRef.current;
                if (frozenDuration > 30000) {
                    if (!video.ended) {
                        if (hlsRef.current) { try { hlsRef.current.startLoad(); } catch (e) {} }
                    }
                    lastChangeRef.current = Date.now();
                }
            } else {
                lastTimeRef.current = currentTime;
                lastChangeRef.current = Date.now();
            }
        }, 5000);

        const handleEnded = () => { onErrorRef.current?.(); };
        video.addEventListener('ended', handleEnded);

        return () => {
            clearInterval(freezeCheckInterval);
            video.removeEventListener('ended', handleEnded);
            if (stalledTimerRef.current) { clearTimeout(stalledTimerRef.current); stalledTimerRef.current = null; }
            if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
        };
    }, [url]);

    return (
        <video ref={videoRef}
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
            muted autoPlay playsInline title={label}
        />
    );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function DisplayApp() {
    const [config, setConfig] = useState<DisplayConfig | null>(null);
    const [nowPlaying, setNowPlaying] = useState<NowPlayingData | null>(null);
    const [overlayData, setOverlayData] = useState<NowPlayingData | null>(null);
    const [overlayReady, setOverlayReady] = useState(true);
    const [larixStalled, setLarixStalled] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [eagleCelebration, setEagleCelebration] = useState<ScoringEventDto | null>(null);
    const leaderboardStarted = useRef(false);
    const nowPlayingKeyRef = useRef<string | null>(null);

    useEffect(() => {
        let retryCount = 0;
        const fetchConfig = async () => {
            try {
                const resp = await fetch(`${API_BASE}/api/display/${CLUB_CODE}`);
                if (!resp.ok) throw new Error(`API ${resp.status}`);
                const data: DisplayConfig = await resp.json();
                setConfig(data);
                setError(null);
                retryCount = 0;
            } catch (e: any) {
                retryCount++;
                if (retryCount >= 3) setError('connecting');
            }
        };
        fetchConfig();
        const interval = setInterval(fetchConfig, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!config?.activeCompetition?.id) return;
        const fetchEvents = async () => {
            try {
                const resp = await fetch(`${API_BASE}/api/display/${CLUB_CODE}`);
                if (!resp.ok) return;
                const data: DisplayConfig = await resp.json();
                if (!data?.activeCompetition) return;
                setConfig(prev => {
                    if (!prev?.activeCompetition) return prev;
                    return {
                        ...prev,
                        activeCompetition: {
                            ...prev.activeCompetition,
                            recentScoringEvents: data.activeCompetition!.recentScoringEvents,
                        }
                    };
                });
            } catch (e) {}
        };
        fetchEvents();
        const interval = setInterval(fetchEvents, 8000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config?.activeCompetition?.id]);

    useEffect(() => {
        if (!config?.clubSlug) return;
        const fetchNowPlaying = async () => {
            try {
                const resp = await fetch(
                    `https://stream.19thview.com.au/clips/${config.clubSlug}/now-playing.json?t=${Date.now()}`
                );
                if (!resp.ok) return;
                const data: NowPlayingData = await resp.json();
                setNowPlaying(prev => {
                    if (prev?.playUrl === data.playUrl &&
                        prev?.type === data.type &&
                        prev?.label === data.label) return prev;
                    return data;
                });
            } catch (e) {}
        };
        fetchNowPlaying();
        const interval = setInterval(fetchNowPlaying, 2000);
        return () => clearInterval(interval);
    }, [config?.clubSlug]);

    useEffect(() => {
        if (!nowPlaying || nowPlaying.type === 'idle') {
            setOverlayData(nowPlaying);
            setOverlayReady(true);
            return;
        }
        const key = `${nowPlaying.type}-${nowPlaying.playUrl}`;
        if (key !== nowPlayingKeyRef.current) {
            const isFirstLoad = nowPlayingKeyRef.current === null;
            const prevType = nowPlayingKeyRef.current?.split('-')[0] ?? null;
            const newType = nowPlaying.type;
            nowPlayingKeyRef.current = key;
            const isTypeChange = !isFirstLoad && prevType !== newType;
            const isAdType = (t: string | null) =>
                t === 'LocalAd' || t === 'PlatformAd' || t === 'SponsorAd' ||
                t === 'ClubNotice' || t === 'beauty';
            if (isTypeChange && !isAdType(newType) && !isAdType(prevType)) {
                if (newType === 'larix') {
                    // Switching TO Larix: keep overlayReady=true
                } else {
                    setOverlayReady(false);
                    setTimeout(() => setOverlayReady(true), 2000);
                }
            }
            setOverlayData(nowPlaying);
        } else {
            setOverlayData(nowPlaying);
        }
    }, [nowPlaying]);

    useEffect(() => {
        setLarixStalled(false);
        setVideoError(false);
    }, [nowPlaying?.playUrl]);

    useEffect(() => {
        if (!config?.activeCompetition) return;
        if (config.activeCompetition.isAdhoc) return;
        if (leaderboardStarted.current) return;
        leaderboardStarted.current = true;
        const settings = config.activeCompetition.displaySettings;
        const rotationMs = settings.leaderboardRotationMinutes * 60 * 1000;
        const durationMs = settings.leaderboardDurationSeconds * 1000;
        const timerId = window.setInterval(() => {
            setEagleCelebration(currentEagle => {
                if (!currentEagle) {
                    setShowLeaderboard(true);
                    window.setTimeout(() => setShowLeaderboard(false), durationMs);
                }
                return currentEagle;
            });
        }, rotationMs);
        (window as any).__leaderboardTimer = timerId;
    }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const reloadInterval = setInterval(() => { window.location.reload(); }, 6 * 60 * 60 * 1000);
        return () => clearInterval(reloadInterval);
    }, []);

    const connectingScreen = (
        <div style={appStyles.errorScreen}>
            <div style={connectStyles.container}>
                <img src="https://stream.19thview.com.au/clips/19thview-logo.png"
                    alt="19thView" style={connectStyles.logo} />
                <div style={connectStyles.divider} />
                <div style={connectStyles.spinner}><div style={connectStyles.spinnerRing} /></div>
                <div style={connectStyles.message}>Connecting to live stream</div>
                <div style={connectStyles.sub}>Stream will begin shortly</div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        </div>
    );

    if (error && !config) return connectingScreen;
    if (!config) return connectingScreen;

    const liveCameras = config.cameras.filter(c => c.isLive);
    const featuredCameraConfig = config.cameras.find(c => c.isFeatured);
    const isFeaturedActive = featuredCameraConfig != null && (
        nowPlaying?.type === 'larix' ||
        (nowPlaying?.label === featuredCameraConfig.label && nowPlaying?.type !== 'idle')
    );
    const hasActiveComp = !!config.activeCompetition;
    const isStreamingWindow = config.activeCompetition?.isStreamingWindow ?? false;
    const streamingActive = config.streamingActive !== false;
    const lbStyle = config.activeCompetition?.displaySettings.leaderboardStyle ?? 'FullScreen';
    const primaryColour = config.primaryColour || '#1a6b3c';
    const playUrl = nowPlaying?.playUrl ?? null;

    const isAdContent = nowPlaying?.type === 'LocalAd' ||
                        nowPlaying?.type === 'PlatformAd' ||
                        nowPlaying?.type === 'SponsorAd' ||
                        nowPlaying?.type === 'ClubNotice' ||
                        nowPlaying?.type === 'beauty';

    const isFtpClip = nowPlaying?.type === 'ftp';

    const showVideo = playUrl && nowPlaying?.type !== 'idle' && (isFtpClip || streamingActive) && !videoError;
    const showIdleLeaderboard = hasActiveComp && (nowPlaying?.type === 'idle' || videoError);

    return (
        <div style={appStyles.root}>

            {eagleCelebration && (
                <EagleCelebration event={eagleCelebration} onDone={() => setEagleCelebration(null)} />
            )}

            {!hasActiveComp && liveCameras.length === 0 && (
                <NoCompetitionScreen clubName={config.clubName} />
            )}

            {!isStreamingWindow && hasActiveComp && !eagleCelebration && (
                <LeaderboardPanel config={config} style="FullScreen" isFinalResults={true} />
            )}

            {config.cameras.length === 0 && hasActiveComp && isStreamingWindow && (
                <AllCamerasOfflineScreen clubName={config.clubName} hasCompetition={hasActiveComp} competitionName={config.activeCompetition?.name} />
            )}

            {showVideo && (
                <>
                    {(!overlayReady || larixStalled) && (
                        <div style={{
                            position: 'absolute', inset: 0, background: '#0a1a10',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: s(32), zIndex: 40,
                            animation: 'fadeIn 0.5s ease',
                        }}>
                            <div style={{ fontSize: s(80), fontWeight: 900, color: '#1a6b3c', letterSpacing: -2, textShadow: '0 0 40px rgba(26,107,60,0.3)' }}>
                                19th<span style={{ color: '#c9a84c' }}>View</span>
                            </div>
                            <StreamConnectingDots />
                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: s(13), letterSpacing: 3, textTransform: 'uppercase', animation: 'pulse 2s ease-in-out infinite' }}>
                                {larixStalled ? 'Stream Ending' : 'Stream Connecting'}
                            </div>
                        </div>
                    )}
                    <div style={{ ...appStyles.videoArea, visibility: (!overlayReady || larixStalled) ? 'hidden' : 'visible' }}>
                        <HlsPlayer
                            key={playUrl!}
                            url={playUrl!}
                            label={nowPlaying?.label ?? ''}
                            onStalled={nowPlaying?.type === 'larix' ? () => setLarixStalled(true) : undefined}
                            onError={nowPlaying?.type === 'beauty' ? () => setVideoError(true) : undefined}
                        />
                    </div>
                </>
            )}

            {showIdleLeaderboard && isStreamingWindow && (
                <LeaderboardPanel config={config} style="FullScreen" />
            )}

            {videoError && !hasActiveComp && config.heroImageUrl && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 30,
                    backgroundImage: `url(${config.heroImageUrl})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
            )}
            {videoError && !hasActiveComp && !config.heroImageUrl && (
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0a1a10 0%, #1a3a20 100%)', zIndex: 30 }} />
            )}

            {showLeaderboard && hasActiveComp && isStreamingWindow && !eagleCelebration &&
            nowPlaying?.type !== 'ftp' && nowPlaying?.type !== 'larix' &&
            nowPlaying?.type !== 'rtmp' && lbStyle === 'Overlay' && (
                <LeaderboardPanel config={config} style="Overlay" />
            )}
            {showLeaderboard && hasActiveComp && isStreamingWindow && !eagleCelebration &&
            nowPlaying?.type !== 'ftp' && nowPlaying?.type !== 'larix' &&
            nowPlaying?.type !== 'rtmp' && lbStyle === 'FullScreen' && (
                <LeaderboardPanel config={config} style="FullScreen" />
            )}

            {/* Top bar */}
            <div style={appStyles.topBar}>
                <div style={appStyles.clubLogo}>
                    {config.logoUrl ? (
                        <img src={config.logoUrl} alt={config.clubName} style={appStyles.clubLogoImg}
                            onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                    ) : (
                        <div style={{ ...appStyles.clubLogoText, background: primaryColour }}>
                            {config.clubName.charAt(0)}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: s(14) }}>
                    {nowPlaying?.type && nowPlaying.type !== 'idle' && !isAdContent && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: s(6), background: '#dc2626', borderRadius: s(6), padding: `${s(4)}px ${s(12)}px`, boxShadow: '0 2px 8px rgba(220,38,38,0.5)' }}>
                            <span style={{ fontSize: s(13), fontWeight: 900, color: '#fff', letterSpacing: 2, textTransform: 'uppercase' as const }}>LIVE</span>
                            <span style={{ width: s(7), height: s(7), borderRadius: '50%', background: '#fff', flexShrink: 0, animation: 'liveBlink 1s ease-in-out infinite', display: 'inline-block' }} />
                        </div>
                    )}
                    <img src="https://stream.19thview.com.au/clips/19thview-logo.png"
                        alt="19thView"
                        style={appStyles.watermark} />
                </div>
            </div>

            {!showLeaderboard && hasActiveComp && isStreamingWindow && streamingActive &&
            nowPlaying?.type !== 'idle' && !isAdContent && (
                <MiniLeaderboard config={config} />
            )}

            {!isAdContent && streamingActive &&
            (nowPlaying?.type === 'ftp' || nowPlaying?.type === 'rtmp' || nowPlaying?.type === 'larix') &&
            nowPlaying?.label && (() => {
                const sponsor = config.activeCompetition?.cameraSponsors?.find(
                    cs => cs.cameraLabel === nowPlaying.label
                );
                return sponsor ? <CameraSponsorBar sponsor={sponsor} /> : null;
            })()}

            {!showLeaderboard && overlayReady && !isAdContent && (
                <HoleOverlay
                    key={`${overlayData?.type}-${overlayData?.playUrl}-${overlayData?.holeNumber}`}
                    data={overlayData}
                    isFeatured={isFeaturedActive}
                />
            )}

            {hasActiveComp && isStreamingWindow && (
                <TickerBar
                    config={config}
                    onEagle={event => { setShowLeaderboard(false); setEagleCelebration(event); }}
                    onBirdie={event => { setShowLeaderboard(false); setEagleCelebration(prev => prev ?? event); }}
                />
            )}

        </div>
    );
}

// ── App Styles ────────────────────────────────────────────────────────────────

const appStyles: Record<string, React.CSSProperties> = {
    root: { width: '100%', height: '100%', background: '#000', overflow: 'hidden', position: 'relative', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    videoArea: { position: 'absolute', inset: 0, bottom: s(72) },
    topBar: { position: 'absolute', top: 0, left: 0, right: 0, height: s(96), display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: `${s(12)}px ${s(20)}px`, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)', zIndex: 200, pointerEvents: 'none' },
    watermark: { height: s(72), maxWidth: s(180), objectFit: 'contain' as const, opacity: 0.85, filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.5))' },
    clubLogoImg: { maxHeight: s(72), maxWidth: s(180), minHeight: s(48), objectFit: 'contain' as const, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.7))' },
    clubLogo: { display: 'flex', alignItems: 'center', height: s(82), minWidth: s(56), padding: `${s(17)}px ${s(16)}px` },
    clubLogoText: { width: s(56), height: s(56), borderRadius: s(10), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: s(26), fontWeight: 800, color: '#fff' },
    cameraLabel: { position: 'absolute', bottom: s(72), left: s(20), background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)', color: '#fff', fontSize: s(22), fontWeight: 800, padding: `${s(10)}px ${s(22)}px`, borderRadius: s(6), display: 'flex', alignItems: 'center', gap: s(10), zIndex: 100, letterSpacing: 1, pointerEvents: 'none', border: '1px solid rgba(255,255,255,0.15)', textTransform: 'uppercase' },
    liveDot: { width: s(10), height: s(10), borderRadius: '50%', flexShrink: 0 },
    loadingScreen: { width: '100%', height: '100%', background: '#0a1a10', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    errorScreen: { width: '100%', height: '100%', background: '#0a1a10', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: s(40) },
    featuredBadge: { fontSize: s(10), fontWeight: 800, background: '#c9a84c', color: '#000', padding: `${s(2)}px ${s(8)}px`, borderRadius: s(3), letterSpacing: 1, textTransform: 'uppercase' },
};

const connectStyles: Record<string, React.CSSProperties> = {
    container: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 },
    logo: { height: s(140), maxWidth: s(480), objectFit: 'contain' as const, marginBottom: s(8) },
    divider: { width: s(60), height: 2, background: 'linear-gradient(to right, transparent, #1a6b3c, transparent)', margin: `${s(16)}px 0 ${s(32)}px` },
    spinner: { width: s(48), height: s(48), marginBottom: s(24), position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    spinnerRing: { width: s(48), height: s(48), borderRadius: '50%', border: `3px solid rgba(26,107,60,0.2)`, borderTopColor: '#1a6b3c', animation: 'spin 1s linear infinite', position: 'absolute' },
    message: { color: 'rgba(255,255,255,0.7)', fontSize: s(18), fontWeight: 500, letterSpacing: 0.5, marginBottom: s(8), animation: 'pulse 2s ease-in-out infinite' },
    sub: { color: 'rgba(255,255,255,0.25)', fontSize: s(13), letterSpacing: 1, textTransform: 'uppercase' },
};
